import { useContext, useRef } from 'react';
import { DateRange, type RangeKeyDict } from 'react-date-range';
import { useObservable, useObservableEagerState } from 'observable-hooks';
import { map, type Observable, switchMap } from 'rxjs';
import { endOfDay, startOfDay } from 'date-fns';
import { DataType } from '@/graphql/types';
import Button from '../../form/Button';
import ExportContext from './ExportContext';
import {
    convertToUtc,
    type SelectionRange,
    useDataSetRanges,
} from '../../../graph/dataset-date-range-picker';
import { startOfOmnidots, useLocale } from '../../../utils/date-fns';
import { schema } from '../../../schema';
import { namingConventions$ } from '../../../utils/naming-conventions';
import Select from '../../form/Select';
import { getDataTypeKeyByValue } from '../../../graph/data-types';

export const ALL_DATA_TYPE = 'all';

export const EXPORT_BUTTONS_TRANSLATIONS = Object.freeze({
    EXPORT: gettext('EXPORT'),
    EXPORT_ALL: gettext('EXPORT_ALL'),
});

const EXPORT_OPTIONS: Record<string, Record<string, unknown>> = {
    [EXPORT_BUTTONS_TRANSLATIONS.EXPORT]: {},
    [EXPORT_BUTTONS_TRANSLATIONS.EXPORT_ALL]: { dataType: ALL_DATA_TYPE },
};

function createDustExportOptionTranslations$(
    options: Record<string, string>
): Observable<Record<string, string>> {
    return namingConventions$.pipe(
        map((namingConventions) =>
            Object.fromEntries(
                Object.entries(options).map(([key, translationKey]) => [
                    key,
                    namingConventions[translationKey],
                ])
            )
        )
    );
}

const pm10ExportOptionTranslations$ = createDustExportOptionTranslations$(schema.pm10ExportOptions);
const pm2p5ExportOptionTranslations$ = createDustExportOptionTranslations$(
    schema.pm2p5ExportOptions
);

function ExportDatePicker() {
    const context = useContext(ExportContext);
    if (!context) {
        throw new Error('ExportDatePicker must be used within ExportContext.Provider');
    }
    const { datePickerExportType, setIsDatePickerOpen, createGraphReport, dataSet } = context;
    const { ranges, setRanges } = useDataSetRanges(dataSet);
    const locale = useLocale();
    const dustExportOptionRef = useRef<HTMLSelectElement>(null);
    const pm10ExportOptionTranslations = useObservableEagerState(pm10ExportOptionTranslations$);
    const pm2p5ExportOptionTranslations = useObservableEagerState(pm2p5ExportOptionTranslations$);
    const currentDataType = useObservableEagerState(dataSet.dataType$);
    const currentDataTypeKey = getDataTypeKeyByValue(currentDataType);
    const currentExportOptionTranslations =
        currentDataTypeKey === DataType.Pm2P5
            ? pm2p5ExportOptionTranslations
            : pm10ExportOptionTranslations;

    const showDustExportOptions = useObservableEagerState(
        useObservable(
            (inputs$) =>
                inputs$.pipe(
                    switchMap(([dataType$]) => dataType$),
                    map((dataType) => {
                        const dataTypeKey = getDataTypeKeyByValue(dataType);
                        return dataTypeKey === DataType.Pm10 || dataTypeKey === DataType.Pm2P5;
                    })
                ),
            [dataSet.dataType$]
        )
    );

    return (
        <div className="flex w-80 flex-col">
            <Button onClick={() => setIsDatePickerOpen(false)} variant="exportButton">
                {gettext('BACK')}
            </Button>
            <DateRange
                onChange={(rangesByKey: RangeKeyDict) => {
                    setRanges([rangesByKey.selection as SelectionRange]);
                }}
                ranges={ranges}
                rangeColors={['#F8DA28']}
                minDate={startOfOmnidots}
                maxDate={new Date()}
                locale={locale}
            />
            {showDustExportOptions && (
                <Select
                    ref={dustExportOptionRef}
                    data={Object.entries(currentExportOptionTranslations ?? {})}
                />
            )}
            {Object.entries(EXPORT_OPTIONS).map(([name, options]) => (
                <Button
                    onClick={() => {
                        createGraphReport(
                            datePickerExportType.current,
                            {
                                ...options,
                                ...(showDustExportOptions
                                    ? { dustType: dustExportOptionRef.current?.value }
                                    : {}),
                            },
                            [
                                convertToUtc(startOfDay(ranges[0].startDate), dataSet).valueOf(),
                                convertToUtc(endOfDay(ranges[0].endDate), dataSet).valueOf(),
                            ]
                        );
                        setIsDatePickerOpen(false);
                    }}
                    variant="exportButton"
                    key={name}
                >
                    {name}
                </Button>
            ))}
        </div>
    );
}

export default ExportDatePicker;
